import React, { FC, useReducer, useEffect, BaseSyntheticEvent, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { isAddress } from 'viem';
import { useAccountAbstractionStore } from '@/features/account-abstraction/store/AccountAbstractionContext';
import { walletsRefreshAction } from '@/services/wallets/walletsActions';
import { LiaExternalLinkAltSolid } from 'react-icons/lia';
import config from '@/common/config';
import Select from '@/components/SelectInput/SelectInput';
import TransferFundsBox from './TransferFundsBox';
import Button, { ButtonSizes, ButtonVariants } from '@/components/new-design/Button';
import ErrorText from '@/components/ErrorText';
import Label from '@/components/Label';
import { transferPol } from '../web3/transferPol';
import useWalletBalance from '@/hooks/useWalletBalance';
import { useWalletAddress } from '@/features/account-abstraction/hooks/useWalletAddress';
import Input from '@/components/Input/Input';
import { transferTokens } from '../web3/transferTokens';

interface State {
  loading: boolean;
  error: boolean;
  errorMessage: string | null;
  success: boolean;
  txHash: string | null;
}

type Action =
  | { type: 'START' }
  | { type: 'SUCCESS'; payload: string }
  | { type: 'ERROR'; payload: string }
  | { type: 'RESET' };

const initialState: State = {
  loading: false,
  error: false,
  errorMessage: null,
  success: false,
  txHash: null,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'START':
      return { ...state, loading: true, error: false, errorMessage: null };
    case 'SUCCESS':
      return {
        ...state,
        loading: false,
        success: true,
        txHash: action.payload,
      };
    case 'ERROR':
      return {
        ...state,
        loading: false,
        error: true,
        errorMessage: action.payload,
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

interface TokensTabProps {
  onClose: () => void;
}

interface FormInputs {
  amount: number;
  walletAddress: `0x${string}`;
  selectedToken: Token;
}

export const validationSchema = (balance: number) =>
  yup
    .object({
      amount: yup
        .number()
        .required('Amount is required')
        .test('balance', 'Insufficient balance', (value) => !!value && value <= balance)
        .transform((value) => {
          if (isNaN(value)) {
            return 0;
          }
          return value;
        }),
      walletAddress: yup
        .mixed<`0x${string}`>()
        .test('address', 'Enter valid address', (value) => !!value && isAddress(value))
        .required('Wallet Address is required'),
      selectedToken: yup
        .object()
        .shape({
          label: yup.string().required('Label is required'),
          address: yup
            .mixed<`0x${string}`>()
            .test('address', 'Enter valid address', (value) => (!!value && isAddress(value)) || value === '0x')
            .required('Not a valid address'),
          img: yup.string().optional(),
          name: yup.string().optional(),
        })
        .required('Token is required'),
    })
    .required();

export interface Token {
  label: string;
  address: `0x${string}`;
  name?: string | undefined;
  img?: string | undefined;
  decimals?: number;
}

const tokens: Token[] = [
  {
    label: 'POL',
    address: '0x',
    img: 'https://polygonscan.com/assets/poly/images/svg/logos/token-dark.svg?v=24.10.1.0',
    decimals: 18,
  },
  {
    label: 'USDT',
    address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
    img: 'https://polygonscan.com/token/images/tether_32.png',
    decimals: 6,
  },
  {
    label: 'USDC',
    address: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
    img: 'https://polygonscan.com/token/images/usdc_32.png',
    decimals: 6,
  },
];

export const TokensTab: FC<TokensTabProps> = ({ onClose }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { walletAddress } = useWalletAddress();

  const [selectedToken, setSelectedToken] = useState<Token | undefined>(tokens[0]);
  const {
    data: selectedTokenBalance,
    // isLoading: isLoadingSelectedToken,
    // isError: isErrorSelectedToken,
  } = useWalletBalance(walletAddress, selectedToken?.address !== '0x' ? selectedToken?.address : undefined);

  const accountAbstractionStore = useAccountAbstractionStore();
  const reduxDispatch = useDispatch();

  useEffect(() => {
    reduxDispatch(walletsRefreshAction());
  }, [reduxDispatch]);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
  } = useForm<FormInputs>({
    resolver: yupResolver<FormInputs>(validationSchema(Number(selectedTokenBalance?.displayBalance) || 0)),
    mode: 'onChange',
    defaultValues: {
      amount: 0,
      selectedToken: tokens[0],
    },
  });

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'selectedToken') {
        setSelectedToken(value.selectedToken as Token);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const onSubmit = async (data: FormInputs, e?: BaseSyntheticEvent<object, any, any>) => {
    e?.preventDefault();
    dispatch({ type: 'START' });
    if (!selectedToken) return;

    try {
      let tx = null;
      if (selectedToken?.address === '0x') {
        tx = await transferPol(accountAbstractionStore.state, data.walletAddress, data.amount);
      } else if (selectedToken?.address !== '0x' && selectedToken?.decimals) {
        tx = await transferTokens(
          accountAbstractionStore.state,
          data.walletAddress,
          data.amount,
          selectedToken?.decimals,
          selectedToken?.address,
        );
      }
      if (tx?.success) {
        dispatch({ type: 'SUCCESS', payload: tx.receipt.transactionHash });
      }
    } catch (e) {
      if (e instanceof Error) {
        dispatch({ type: 'ERROR', payload: e.message });
      } else dispatch({ type: 'ERROR', payload: '' });
    }
  };

  const handleRetry = () => {
    dispatch({ type: 'RESET' });
  };

  console.log('selectedTokenBalance', { selectedTokenBalance, errors, state, isValid });

  return (
    <div className="left-4 flex w-full min-w-[min(650px,calc(100vw-50px))] max-w-[min(650px,calc(100vw-50px))] items-center justify-center">
      <div className="flex h-full w-full flex-col items-center justify-center gap-5 rounded-[30px] bg-ph-background-dark">
        <div className="flex w-full flex-col">
          <h3 className="text-lg text-ph-gray-white">Transfer Tokens</h3>
          <p className="text-sm text-ph-white-30">Transfer tokens from your wallet to your friends or other users</p>
        </div>
        {!state.success && !state.error && (
          <>
            <form onSubmit={handleSubmit(onSubmit)} className="w-full">
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-1">
                  <Label semiBold>Choose Token</Label>
                  <div className="relative">
                    <Select
                      options={tokens}
                      onChange={(value: Token) => {
                        setValue('selectedToken', value, { shouldValidate: true });
                      }}
                      displayValue={(token: Token) => token?.label}
                      value={selectedToken}
                      renderOption={(option: Token) => (
                        <div className="flex items-center gap-2">
                          <img src={option.img} alt={option.label} width={24} />
                          <span>{option.label}</span>
                        </div>
                      )}
                    />
                  </div>
                  {errors.selectedToken && <ErrorText>* {errors.selectedToken.message}</ErrorText>}
                </div>
                <div className="flex flex-col gap-1">
                  <Label semiBold>Transfer to</Label>
                  <div className="relative">
                    {/* <img src={wallet} alt="wallet" className="absolute left-6 top-2.5 z-30" width={24} /> */}
                    <Input fullWidth {...register('walletAddress')} />
                  </div>
                  {errors.walletAddress && <ErrorText>* {errors.walletAddress.message}</ErrorText>}
                </div>
                <TransferFundsBox
                  isValid={!!isValid}
                  state={state}
                  onClose={onClose}
                  loadingLabel="Processing"
                  balance={Number(selectedTokenBalance.displayBalance)}
                  register={register}
                  errors={errors}
                  token={selectedToken}
                />
              </div>
            </form>
          </>
        )}
        {state.error && (
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-bold text-ph-red">Transaction Failed:</p>
              <span className="flex items-center gap-1">
                {state.errorMessage ? (
                  <p title={state.errorMessage || ''} className="overflow-hidden overflow-ellipsis text-sm text-ph-red">
                    {state.errorMessage}
                  </p>
                ) : (
                  <p className="overflow-hidden overflow-ellipsis text-sm text-ph-red">
                    There was an error with your transaction. If this keeps happening please contact{' '}
                    <a type="email" className="text-ph-blue" href="mailto:support@phenompoker.com">
                      support@phenompoker.com
                    </a>
                  </p>
                )}
              </span>
            </div>
            <div className="flex gap-1">
              <Button onClick={handleRetry} variant={ButtonVariants.primary} size={ButtonSizes.lg}>
                Try Again
              </Button>
              <Button onClick={onClose} variant={ButtonVariants.secondary} size={ButtonSizes.lg}>
                Close
              </Button>
            </div>
          </div>
        )}
        {state.success && (
          <div className="flex flex-col gap-5">
            <h3 className="text-lg text-ph-gray-white">Transaction Submitted!</h3>
            <a
              title={`${config.blockExplorerUrl}/tx/${state.txHash}`}
              href={`${config.blockExplorerUrl}/tx/${state.txHash}`}
              target="_blank"
              rel="noreferrer"
              className="text-ph-blue"
            >
              <span className="flex items-center gap-1">
                <p>View on Block Explorer </p>
                <LiaExternalLinkAltSolid size={20} className="text-ph-blue" />
              </span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
